import Vue from 'vue'
import store from '@/store'
import GeolocationManager from '../../geolocationManager'
import config from '@/config'
import moment from 'moment'
import { localeProperties } from '@/library/locale/i18n'

const state = config.app.autoconf.STATE
const customField = config.walletParams.customFieldKey
const errorMsgCode = [615, 612, 602, 604, 605]
const installerKey = config.walletParams.installerKey
const isDebug = sessionStorage.geolocationDebug
let customFieldValue = null
let initPeriodicCheck = null
let userLicense
let storeIsGeolocated
let geoManager = null

function startGeolocation (user, sessionId, reason = 'Login') {
  if (reason === 'IP changed') {
    clearPeriodicCheckInterval()
    requestGeoLocation(user, reason)
    return false
  }
  resetGeoError(reason)
  geoComplyDisconnectService(reason)
  onPluginConnected(user, sessionId, reason)
  connectGeoComplyService(user, sessionId, reason)
}

/** --------------------------------
 *  before starting geolocation again, unset geo error
 -------------------------------- **/
function resetGeoError (reason) {
  if (isDebug && reason !== 'Login') console.log('[GeoComply] reset GeoError.')
  if (typeof localStorage !== 'undefined') {
    if (localStorage.getItem('geoError')) {
      localStorage.setItem('geoError', false)
    }
  }
}

/** --------------------------------
 *  disconnect from geocomply service on: logout, failed geolocation
 -------------------------------- **/
function geoComplyDisconnectService (reason) {
  if (isDebug && reason !== 'Login') {
    console.log('[GeoComply] Disconnecting PLC service...')
    setTimeout(() => {
      if (!window.GeoComply.Client.isConnected()) console.log('[GeoComply] PLC service is disconnected.')
    }, 2000)
  }
  store.commit('setGeolocated', false)
  localStorage.setItem('gcPerm', false)
  turnOffEvents(reason)
  clearTimeout(initPeriodicCheck)
  window.GeoComply.Client.disconnect()
}

/** --------------------------------
 *  turn off all geoComply events
 -------------------------------- **/
function turnOffEvents (reason) {
  if (!window.GeoComply) return false
  window.GeoComply.Client.off('connect').off('geolocation').off('error').off('log')
}

/** --------------------------------
 *  establish connection with geoComply service via webSocket
 -------------------------------- **/
function connectGeoComplyService (user, sessionId, reason) {
  if (isDebug && reason === 'Login') console.log('[GeoComply] Connecting to PLC Service...')
  window.GeoComply.Client.connect(environment().id, environment().env)
}

/** --------------------------------
 *  show spinner on geolocaton start
 -------------------------------- **/
function showSpinnerOnStart () {
  if (!JSON.parse(store.getters['getGeolocated'])) {
    var errorCode = 'verify'
    store.commit('setGeolocationOnLogin', true)
    store.commit('overlayState/setGeolocationMessage', { errorCode })
  }
}
/** --------------------------------
 *  set download plugin key for specific environment
 -------------------------------- **/
function environment () {
  var gcData = {}
  if (process.env.NODE_ENV === 'development') {
    gcData = { env: 'staging', id: installerKey }
    return gcData
  } else if (process.env.NODE_ENV === 'production') {
    gcData = { env: 'production', id: installerKey }
    return gcData
  } else {
    gcData = { env: 'staging', id: installerKey }
    return gcData
  }
}

/** --------------------------------
 *  check license expiration date; valid - continue with geolocating ; invalid - request new license
 -------------------------------- **/
function validateLicense (userID, sessionId, reason) {
  if (isDebug) console.log('[GeoComply] Validating license from storage...')
  let licenseDate
  let currentDate = new Date()
  let storedMillis = JSON.parse(localStorage.getItem('userLicense' + state)).ExpiresInMilliseconds
  let storedDate = new Date(JSON.parse(localStorage.getItem('userLicense' + state)).logDate)
  licenseDate = new Date(storedDate.getTime() + storedMillis)
  if (licenseDate < currentDate) {
    if (isDebug) console.log('[GeoComply] License expired.')
    managerInstance.geolocationManager.getLicense(userID, sessionId, reason)
  }
  if (licenseDate > currentDate) {
    if (isDebug) console.log('[GeoComply] License is valid.')
    requestGeoLocation(userID, reason)
  }
}
/** --------------------------------
 *  request geoPacket from geocomply service
 -------------------------------- **/
function requestGeoLocation (userID, reason) {
  store.commit('setOnLoginGeolocationProgress', 'request_geoLocation')
  let userId = userID.toString()
  window.GeoComply.Client.setUserId(userId)
  if (customField) {
    for (let field = 0; field < customField.length; field++) {
      if (customField[field] === 'language') {
        customFieldValue = localeProperties.getUserLocale.toUpperCase()
        window.GeoComply.Client.customFields.set('language', customFieldValue)
      }
    }
  }
  window.GeoComply.Client.setGeolocationReason('Desktop: Sports/' + reason)
  if (isDebug) console.log('[GeoComply] Getting location...')
  window.GeoComply.Client.requestGeolocation()
}

function validateResponse (result, userID) {
  store.commit('setOnLoginGeolocationProgress', 'validate_geo_response')
  sessionStorage.removeItem('tempPromotionSection')
  if (result.IsValid) {
    locationVerified(result, userID)
  }
  if (!result.IsValid) {
    locationFailed(result, userID)
    store.commit('setGeocomplyTroubleshooter', result.Troubleshooter)
  }
}

/** --------------------------------
 *  location verified ; allow user to place bet, remove error messages, prepare periodic location check
 -------------------------------- **/
function locationVerified (response, userID) {
  store.commit('setOnLoginGeolocationProgress', 'completed')
  /*
   Recommendation: DOCS in INWLT1458
   The buffer time should be adjusted to:
   ○ ~30% of the “Re-geolocate in” value for the small intervals less than 120s
   ○ 35 sec. of the “Re-geolocate in” value for the larger intervals more that
   */
  let bufferedTime
  if (response.BufferTime) { // f buffer does exist in response
    bufferedTime = response.NextGeolocationInSeconds - response.BufferTime
  }
  if (!response.BufferTime && response.NextGeolocationInSeconds < 120) { // if buffer does not exist in response, next geolocation is less than 120 sec, decrease for ~ 30%
    let buffer = (response.NextGeolocationInSeconds / 100) * 30
    bufferedTime = response.NextGeolocationInSeconds - buffer
  }
  if (!response.BufferTime && response.NextGeolocationInSeconds > 120) { // if buffer does not exist in response, next geolocation is more than 120 sec, decrease for 35 sec
    bufferedTime = response.NextGeolocationInSeconds - 35
  }
  if (isDebug) console.log('[GeoComply] Location is verified.')
  if (response.NewLicence !== null) {
    window.GeoComply.Client.setLicense(response.NewLicence.License)
    response.NewLicence['logDate'] = new Date()
    localStorage.setItem('userLicense' + state, JSON.stringify(response.NewLicence))
  }
  let currentTime = moment()
  let timer = bufferedTime * 1000 // convert to milliseconds
  localStorage.removeItem('tgc')
  let lcData = { 'date': currentTime, 'cf': timer }
  localStorage.setItem('tgc', JSON.stringify(lcData))
  localStorage.setItem('gcPerm', true)
  store.commit('setGeolocated', true)
  setTimeout(() => {
    if (localStorage.geolocationOnLogin) {
      store.dispatch('triggerBSLocationVerifiedAnimation', true)
      localStorage.removeItem('geolocationOnLogin')
    }
  }, 100)
  localStorage.setItem('geoError', false)
  setTimeout(() => {
    store.commit('setGeolocationOnLogin', false)
  }, 6000)
  store.commit('overlayState/setGeolocationMessage', {})
  clearTimeout(initPeriodicCheck)
  initPeriodicCheck = setTimeout(() => {
    sessionStorage.geoReason = 'periodic'
    requestGeoLocation(userID, 'Periodic')
  }, timer)
}

/** --------------------------------
 *  location failed ; remove permission, display errors
 -------------------------------- **/
function locationFailed (response, userID) {
  if (isDebug) console.log('[GeoComply] Location not verified.', response.Troubleshooter)
  store.commit('setOnLoginGeolocationProgress', 'completed')
  localStorage.removeItem('geolocationOnLogin')
  localStorage.setItem('gcMessages', JSON.stringify(response.Troubleshooter))
  let errorCode = 999
  store.commit('setGeolocationOnLogin', false)
  displayErrorMessage(errorCode, response.Troubleshooter, userID)
}

function errorHandler (errorCode, errorMessage, userID, reason) {
  geocomplyTechnicalError(errorCode, errorMessage, userID, reason)
  var isCode = errorMsgCode.includes(errorCode)
  if (isCode) {
    errorCode = parseInt(errorCode)
    store.commit('setGeolocated', false)
    localStorage.setItem('gcPerm', false)
    if (errorMsgCode.includes(612)) {
      localStorage.setItem('geoError', 612)
    }
    displayErrorMessage(errorCode, errorMessage, userID)
  }
  switch (parseInt(errorCode)) {
    case window.GeoComply.Client.CLNT_ERROR_LOCAL_SERVICE_COMMUNICATION:
      if (isDebug) console.log('Communication with PLC (local service) failed. The possible cause of this is that either some invalid data was sent to PLC or some problems with parsing data from PLC')
      break
    case window.GeoComply.Client.CLNT_ERROR_WRONG_OR_MISSING_PARAMETER:
      if (isDebug) console.log('parameters are missing or provided with wrong values')
      break
    case window.GeoComply.Client.CLNT_ERROR_INVALID_LICENSE_FORMAT:
    case window.GeoComply.Client.CLNT_ERROR_LICENSE_EXPIRED:
      if (isDebug) console.log(' License expired, request new license')
      managerInstance.geolocationManager.getLicense(userID, null, reason)
      break
    case window.GeoComply.Client.CLNT_ERROR_SERVER_COMMUNICATION:
      if (isDebug) console.log('The server communication failed')
      break
    case window.GeoComply.Client.CLNT_ERROR_UNEXPECTED:
      store.commit('setGeolocated', false)
      localStorage.setItem('gcPerm', false)
      geoComplyDisconnectService()
      if (isDebug) console.log('Unexpected error occurs.')
      break
    case window.GeoComply.Client.CLNT_ERROR_NOT_CERTIFIED_BINARIES:
      if (isDebug) console.log('Signature verification of PLC support binaries fails. That means the binaries tampered.')
      break
    case window.GeoComply.Client.CLNT_ERROR_CLIENT_LICENSE_UNAUTHORIZED:
      if (isDebug) console.log('Operator’s license not authorized by GeoComply server.')
      break
    case window.GeoComply.Client.CLNT_ERROR_INVALID_CUSTOM_FIELDS:
      if (isDebug) console.log('Invalid list of custom fields provided to SDK. This can be because of mandatory custom field is absent in the list, or format of custom field value is invalid.')
      break
    default:
      store.commit('setGeolocated', false)
      localStorage.setItem('gcPerm', false)
  }
}

/** --------------------------------
 *  user is not geolocated or plugin not exist ; provide message to the user
 -------------------------------- **/
function displayErrorMessage (errorCode, errorMessage, userID) {
  if (isDebug) console.log('[GeoComply] location / plugin error message.')
  localStorage.setItem('geoError', errorCode)
  geoComplyDisconnectService()
  store.commit('overlayState/setGeolocationMessage', { errorCode, errorMessage, userID })
  store.dispatch('overlayState/activeGeolocationDialog')
}

function geocomplyTechnicalError (errorCode, errorMessage, userID, reason) {
  let keyName = getErrorKey(parseInt(errorCode))
  let logReason = 'Desktop: Sports/ ' + reason
  let attemptID = store.getters['getGeolocationAttempt']
  store.dispatch('completeGeolocAttempt', {errorCode, errorMessage, userID, logReason, keyName, attemptID})
}

function getErrorKey (value) {
  let object = window.GeoComply.Client
  return Object.keys(object).find(key => object[key] === value)
}

function clearPeriodicCheckInterval () {
  clearInterval(initPeriodicCheck)
}
function continueGeolocationProcess () {
  let userID = localStorage.customer
  let sessionId = null
  let webViewAndMobile = store.getters['getWebViewAndMobile']
  let isFromWebView = store.getters['getIsOpenedFromWebView']
  store.commit('setGeolocated', JSON.parse(localStorage.getItem('gcPerm')))
  let isGeolocated = JSON.parse(localStorage.getItem('gcPerm'))
  let reason = 'Periodic'
  if (!webViewAndMobile && (!isFromWebView && isGeolocated)) {
    sessionStorage.geoReason = 'periodic'
    if (isDebug) console.log('[GeoComply] Checking geolocation on refresh...')
    let currentTime = moment()
    let storedTime = JSON.parse(localStorage.getItem('tgc')).date
    let checkFor = JSON.parse(localStorage.getItem('tgc')).cf
    let diffTime = moment.duration(currentTime.diff(storedTime)).asMilliseconds()
    if (diffTime > checkFor) {
      if (isDebug) console.log('[GeoComply] Geolocation token expired. Init geolocation check.')
      managerInstance.geolocationManager.initGeolocation(userID, sessionId, reason)
    } else if (diffTime < checkFor) {
      if (isDebug) console.log('[GeoComply] Geolocation token is valid.')
      let getDiff = checkFor - diffTime
      setTimeout(() => {
        managerInstance.geolocationManager.initGeolocation(userID, sessionId, reason)
      }, getDiff)
    }
  }
}

function onPluginConnected (user, sessionId, reason) {
  window.GeoComply.Client.on('connect', () => {
    if (isDebug) console.log('[GeoComply SDK] Connected.')
    if (reason === 'Login' || reason === 'Retry') {
      showSpinnerOnStart()
    }
    if (reason === 'Login') {
      localStorage.geolocationOnLogin = true
    }
    window.GeoComply.Client.setLicense('')
    if (typeof localStorage !== 'undefined') {
      userLicense = ('userLicense' + state in localStorage) ? localStorage.getItem('userLicense' + state) : localStorage.setItem('userLicense' + state, undefined)
      storeIsGeolocated = ('gcPerm' in localStorage) ? localStorage.getItem('gcPerm') : localStorage.setItem('gcPerm', false)
      store.commit('setGeolocated', storeIsGeolocated)
      if (userLicense === 'undefined' || userLicense === undefined) {
        managerInstance.geolocationManager.getLicense(user, sessionId, reason)
      } else if (userLicense !== undefined) {
        if (isDebug) {
          console.log('[GeoComply] Get license from storage:')
          console.log(JSON.parse(userLicense).License)
        }
        window.GeoComply.Client.setLicense(JSON.parse(userLicense).License)
        validateLicense(user, sessionId, reason)
      }
    } else {
      managerInstance.geolocationManager.getLicense(user, sessionId, reason)
    }
  }).on('error', (errorCode, errorMessage) => {
    if (isDebug) console.log('[GeoComply] SERVICE ERROR:', {errorCode, errorMessage})
    errorHandler(errorCode, errorMessage, user, reason)
  }).on('geolocation', (data) => {
    if (isDebug) {
      console.log('[GeoComply] geoPacket:')
      console.log(data)
    }
    managerInstance.geolocationManager.validateGeolocation(data, user)
  }).on('log', (log) => {
    if (isDebug) console.log('[GeoComply] SERVICE LOG:', log)
  })
}

function downloadPlugin () {
  let walletParams = config.walletParams
  let userID = localStorage.customer
  let navigatorID
  if (navigator.appVersion.indexOf('Win') !== -1) {
    navigatorID = 'win'
  } if (navigator.appVersion.indexOf('Mac') !== -1) {
    navigatorID = 'mac'
  }
  const evergreenVersion = walletParams.geocomplyPluginVersion.split('.')[0]
  let link = 'https://' + walletParams.installerURL + '/installer/url?id=' + walletParams.installerKey + '&os=' + navigatorID + '&version=' + evergreenVersion + '&user_id=' + userID
  if (isDebug) console.log('[GeoComply] Downloading geolocation plugin...', link)
  window.location = link
}

function disconnectService () {
  if (!window.GeoComply) return false
  if (isDebug) {
    console.log('[GeoComply] Disconnecting PLC service...')
    setTimeout(() => {
      if (!window.GeoComply.Client.isConnected()) console.log('[GeoComply] PLC service is disconnected.')
    }, 2000)
  }
  store.commit('setGeolocated', false)
  localStorage.setItem('gcPerm', false)
  turnOffEvents()
  clearTimeout(initPeriodicCheck)
  window.GeoComply.Client.disconnect()
}

const managerInstance = {
  get geolocationManager () {
    if (!geoManager) geoManager = GeolocationManager.getInstance(config.app.autoconf.GEOLOCATION.PROVIDER, Vue.prototype.$geolocationDebug)
    return geoManager
  }
}

export default {
  startGeolocation,
  requestGeoLocation,
  validateResponse,
  geoComplyDisconnectService,
  clearPeriodicCheckInterval,
  continueGeolocationProcess,
  downloadPlugin,
  disconnectService
}



// WEBPACK FOOTER //
// ./src/library/geolocation/services/providers/geocomply/helpers.js